import type { MapId } from './maps'

export type ThreatLevel = 0 | 1 | 2 | 3
export type ThreatTrend = 'STABLE' | 'ESCALATED' | 'EASED'
export type CampaignModifierId = 'CAPITAL_CONTROLS' | 'ARMORED_LEDGER' | 'RAPID_CONSENSUS'

export interface ThreatProfile {
  readonly level: ThreatLevel
  readonly name: string
  readonly glyph: string
  readonly color: string
  readonly hpMultiplier: number
  readonly speedMultiplier: number
  readonly rewardMultiplier: number
  readonly scoreBonus: number
}

export interface CampaignModifier {
  readonly id: CampaignModifierId
  readonly name: string
  readonly codename: string
  readonly glyph: string
  readonly description: string
  readonly effect: string
  readonly color: string
  readonly startingCapitalMultiplier: number
  readonly hpMultiplier: number
  readonly speedMultiplier: number
  readonly spawnRateMultiplier: number
  readonly rewardMultiplier: number
  readonly armorBonus: number
}

export const THREAT_PROFILES: Record<ThreatLevel, ThreatProfile> = {
  0: { level: 0, name: 'ROUTINE', glyph: '◇', color: '#6f929e', hpMultiplier: 1, speedMultiplier: 1, rewardMultiplier: 1, scoreBonus: 0 },
  1: { level: 1, name: 'ELEVATED', glyph: '△', color: '#35d8ea', hpMultiplier: 1.1, speedMultiplier: 1.03, rewardMultiplier: 1.08, scoreBonus: 250 },
  2: { level: 2, name: 'SEVERE', glyph: '⬡', color: '#ffcf76', hpMultiplier: 1.22, speedMultiplier: 1.07, rewardMultiplier: 1.17, scoreBonus: 550 },
  3: { level: 3, name: 'CRITICAL', glyph: '◆', color: '#ff6179', hpMultiplier: 1.38, speedMultiplier: 1.12, rewardMultiplier: 1.3, scoreBonus: 900 },
}

export const CAMPAIGN_MODIFIERS: Record<CampaignModifierId, CampaignModifier> = {
  CAPITAL_CONTROLS: {
    id: 'CAPITAL_CONTROLS', name: 'CAPITAL CONTROLS', codename: 'SCARCITY', glyph: '₮', color: '#ffcf76',
    description: 'Restricted opening liquidity forces a leaner defense grid, but hostile bounties rise.',
    effect: '-15% starting USDT // +25% bounties',
    startingCapitalMultiplier: 0.85, hpMultiplier: 1, speedMultiplier: 1, spawnRateMultiplier: 1, rewardMultiplier: 1.25, armorBonus: 0,
  },
  ARMORED_LEDGER: {
    id: 'ARMORED_LEDGER', name: 'ARMORED LEDGER', codename: 'FORTIFIED', glyph: '⬢', color: '#8b73ff',
    description: 'Consensus hardening gives every hostile an additional armor layer and a higher bounty.',
    effect: '+8% hostile armor // +18% bounties',
    startingCapitalMultiplier: 1, hpMultiplier: 1, speedMultiplier: 1, spawnRateMultiplier: 1, rewardMultiplier: 1.18, armorBonus: 0.08,
  },
  RAPID_CONSENSUS: {
    id: 'RAPID_CONSENSUS', name: 'RAPID CONSENSUS', codename: 'ACCELERATED', glyph: '↯', color: '#ff4fd8',
    description: 'Compressed block timing accelerates every hostile route and formation cadence.',
    effect: '+12% speed // +15% spawn rate // +20% bounties',
    startingCapitalMultiplier: 1, hpMultiplier: 1, speedMultiplier: 1.12, spawnRateMultiplier: 1.15, rewardMultiplier: 1.2, armorBonus: 0,
  },
}

export const CAMPAIGN_MODIFIER_IDS = Object.keys(CAMPAIGN_MODIFIERS) as CampaignModifierId[]

export function campaignModifierFor(mapId: MapId, seed: number): CampaignModifier {
  const mapWeight: Record<MapId, number> = { GENESIS_NETWORK: 0, NEON_EXCHANGE: 1, VALIDATOR_HEIGHTS: 2 }
  return CAMPAIGN_MODIFIERS[CAMPAIGN_MODIFIER_IDS[(Math.abs(seed) + mapWeight[mapId]) % CAMPAIGN_MODIFIER_IDS.length]]
}

export function initialThreatLevel(accountLevel: number, requiredLevel: number, clears: number, bestStars: number): ThreatLevel {
  const mastery = (clears >= 1 ? 1 : 0) + (clears >= 3 || bestStars >= 3 ? 1 : 0) + (accountLevel >= requiredLevel + 4 ? 1 : 0)
  return Math.min(2, mastery) as ThreatLevel
}

export function adjustedThreatLevel(current: ThreatLevel, coreLoss: number, coreIntegrity: number): { level: ThreatLevel; trend: ThreatTrend } {
  if (coreLoss === 0 && coreIntegrity >= 85 && current < 3) return { level: (current + 1) as ThreatLevel, trend: 'ESCALATED' }
  if (coreLoss >= 15 && current > 0) return { level: (current - 1) as ThreatLevel, trend: 'EASED' }
  return { level: current, trend: 'STABLE' }
}
